import { Check } from "lucide-react";
import { Button } from "../../../../ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../ui/popover";
import { Checkbox } from "../../../../ui/checkbox";
import { Badge } from "../../../../ui/badge";

interface Outlet {
  id: string;
  outlet_name: string;
  location?: string;
}

interface OutletMultiSelectProps {
  outlets: Outlet[];
  selectedOutlets: string[];
  onChange: (selected: string[]) => void;
}

export const OutletMultiSelect = ({
  outlets,
  selectedOutlets,
  onChange,
}: OutletMultiSelectProps) => {
  const isAllSelected = selectedOutlets.length === 0 || selectedOutlets.includes("all");

  const handleToggleAll = () => {
    if (isAllSelected) {
      onChange([]);
    } else {
      onChange(["all"]);
    }
  };

  const handleToggleOutlet = (outletId: string) => {
    if (isAllSelected) {
      // If "all" was selected, switch to just this outlet
      onChange([outletId]);
    } else {
      if (selectedOutlets.includes(outletId)) {
        // Remove this outlet
        const newSelection = selectedOutlets.filter((id) => id !== outletId);
        onChange(newSelection.length === 0 ? ["all"] : newSelection);
      } else {
        // Add this outlet
        onChange([...selectedOutlets, outletId]);
      }
    }
  };

  const getDisplayText = () => {
    if (isAllSelected) return "All Outlets";
    if (selectedOutlets.length === 1) {
      const outlet = outlets.find((o) => o.id === selectedOutlets[0]);
      return outlet?.outlet_name || "Select outlets";
    }
    return `${selectedOutlets.length} outlets selected`;
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-[280px] justify-between bg-card"
        >
          <span className="truncate">{getDisplayText()}</span>
          <Badge variant="secondary" className="ml-2 rounded-full">
            {isAllSelected ? outlets.length : selectedOutlets.length}
          </Badge>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0 bg-card" align="start">
        <div className="max-h-[300px] overflow-y-auto">
          <div
            className="flex items-center gap-2 px-3 py-2 hover:bg-muted/50 cursor-pointer border-b"
            onClick={handleToggleAll}
          >
            <Checkbox checked={isAllSelected} />
            <span className="font-medium">All Outlets</span>
            {isAllSelected && <Check className="ml-auto h-4 w-4 text-primary" />}
          </div>
          
          {outlets.map((outlet) => {
            const isSelected = !isAllSelected && selectedOutlets.includes(outlet.id);
            return (
              <div
                key={outlet.id}
                className="flex items-center gap-2 px-3 py-2 hover:bg-muted/50 cursor-pointer"
                onClick={() => handleToggleOutlet(outlet.id)}
              >
                <Checkbox checked={isSelected} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{outlet.outlet_name}</div>
                  {outlet.location && (
                    <div className="text-xs text-muted-foreground truncate">
                      {outlet.location}
                    </div>
                  )}
                </div>
                {isSelected && <Check className="h-4 w-4 text-primary flex-shrink-0" />}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
